/**
 * Assigned points against weekly capacity: contractors down, weeks across.
 *
 * ## Over-capacity is never signalled by colour alone
 *
 * The entire job of this screen is spotting an oversubscribed week from across
 * the room, and a red cell does nothing for the ~8% of men who will not see it
 * as red, or for a greyscale print of a status email. So an over-committed
 * cell carries four independent signals:
 *
 *   1. the diagonal hatch (`.hatch-blocked`),
 *   2. a warning glyph,
 *   3. the words "Over by 2.5",
 *   4. a meter whose fill visibly crosses the capacity rule.
 *
 * Any one of the four is sufficient. Colour is the fifth.
 *
 * ## Unestimated work is shown, not folded in
 *
 * The API deliberately leaves an unestimated task out of `assignedPoints` and
 * counts it in `unestimatedTasks` instead, because inventing a number would be
 * a lie. A grid that then hides that count inherits the lie: the week reads as
 * having headroom it does not have. So every cell that has one says so.
 *
 * ## No money, ever
 *
 * A contractor may read this screen - the team plans together - and points are
 * not pay. Nothing in this file formats cents.
 */
import type { CapacityCell, CapacityReport, PlainDate } from '@botguide/shared';
import { formatWeekLabel } from '@botguide/shared';

import { Icon } from '@/components';
import { cn } from '@/lib/cn';
import { formatCount, formatPoints } from '@/lib/format';

export interface CapacityGridProps {
  report: CapacityReport;
  /** The Monday of the real current week, so "now" is marked in the header. */
  thisWeek: PlainDate;
  /** Marks the viewer's own row. Null for an owner looking at the team. */
  currentUserId?: string | null;
}

interface PersonRow {
  userId: string;
  userName: string;
  cells: CapacityCell[];
}

/** Rows in the order the API returned people, with one cell per week. */
function rowsOf(report: CapacityReport): PersonRow[] {
  const byUser = new Map<string, PersonRow>();

  for (const cell of report.cells) {
    const row = byUser.get(cell.userId);
    if (row) row.cells.push(cell);
    else byUser.set(cell.userId, { userId: cell.userId, userName: cell.userName, cells: [cell] });
  }

  const order = new Map(report.weeks.map((week, index) => [week, index]));
  for (const row of byUser.values()) {
    row.cells.sort((a, b) => (order.get(a.weekStart) ?? 0) - (order.get(b.weekStart) ?? 0));
  }

  return [...byUser.values()];
}

/** What the page prints above the grid. Derived here so both agree. */
export interface CapacitySummary {
  overCells: number;
  overPeople: number;
  unestimatedTasks: number;
  unassignedTasks: number;
  unassignedPoints: number;
}

export function summariseCapacity(report: CapacityReport): CapacitySummary {
  const over = report.cells.filter((cell) => cell.overBy > 0);
  return {
    overCells: over.length,
    overPeople: new Set(over.map((cell) => cell.userId)).size,
    unestimatedTasks: report.cells.reduce((sum, cell) => sum + cell.unestimatedTasks, 0),
    unassignedTasks: report.unassigned.reduce((sum, row) => sum + row.taskCount, 0),
    unassignedPoints: report.unassigned.reduce((sum, row) => sum + row.points, 0),
  };
}

/* -------------------------------------------------------------------------- */

const STICKY = 'sticky left-0 z-10 w-40 min-w-40 max-w-40 sm:w-48 sm:min-w-48 sm:max-w-48';

export function CapacityGrid({ report, thisWeek, currentUserId }: CapacityGridProps) {
  const rows = rowsOf(report);

  const teamByWeek = report.weeks.map((week) => {
    const cells = report.cells.filter((cell) => cell.weekStart === week);
    return {
      week,
      assigned: cells.reduce((sum, cell) => sum + cell.assignedPoints, 0),
      capacity: cells.reduce((sum, cell) => sum + cell.capacityPoints, 0),
    };
  });

  return (
    <div className="scroll-x rounded-lg border border-line bg-surface">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">
          Assigned story points against weekly capacity, by person and by week
        </caption>

        <thead>
          <tr className="border-b border-line">
            <th scope="col" className={cn(STICKY, 'bg-paper px-3 py-2.5 text-left')}>
              <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-subtle">
                Contractor
              </span>
            </th>
            {report.weeks.map((week) => (
              <th
                key={week}
                scope="col"
                className="min-w-36 border-l border-line bg-paper/60 px-3 py-2.5 text-left align-bottom"
              >
                <span className="block font-mono text-[0.75rem] text-ink tnum">
                  {formatWeekLabel(week)}
                </span>
                <span
                  className={cn(
                    'mt-0.5 block text-[0.625rem] font-semibold uppercase tracking-[0.12em]',
                    week === thisWeek ? 'text-accent-ink' : 'text-subtle',
                  )}
                >
                  {week === thisWeek ? 'This week' : 'Week of'}
                </span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => (
            <tr key={row.userId} className="border-b border-line">
              <th scope="row" className={cn(STICKY, 'bg-surface px-3 py-3 text-left align-top')}>
                <span className="block truncate text-sm font-medium">{row.userName}</span>
                {row.userId === currentUserId ? (
                  <span className="mt-0.5 block text-[0.6875rem] font-semibold uppercase tracking-[0.12em] text-accent-ink">
                    You
                  </span>
                ) : (
                  <span className="mt-0.5 block font-mono text-[0.6875rem] text-subtle tnum">
                    {formatPoints(row.cells[0]?.capacityPoints ?? 0)} pts/wk
                  </span>
                )}
              </th>

              {row.cells.map((cell) => (
                <Cell key={cell.weekStart} cell={cell} isThisWeek={cell.weekStart === thisWeek} />
              ))}
            </tr>
          ))}
        </tbody>

        <tfoot>
          <tr className="border-b border-line bg-paper/60">
            <th scope="row" className={cn(STICKY, 'bg-paper px-3 py-2.5 text-left')}>
              <span className="text-[0.6875rem] font-semibold uppercase tracking-[0.1em] text-subtle">
                Team total
              </span>
            </th>
            {teamByWeek.map((total) => (
              <td key={total.week} className="border-l border-line px-3 py-2.5 align-middle">
                <span
                  className={cn(
                    'font-mono text-[0.8125rem] tnum',
                    total.assigned > total.capacity ? 'font-semibold text-danger-ink' : 'text-muted',
                  )}
                >
                  {formatPoints(total.assigned)}
                  <span className="text-subtle"> / {formatPoints(total.capacity)}</span>
                </span>
              </td>
            ))}
          </tr>

          <tr>
            <th scope="row" className={cn(STICKY, 'bg-surface px-3 py-3 text-left align-top')}>
              <span className="block text-sm font-medium">Unassigned</span>
              <span className="mt-0.5 block text-[0.6875rem] text-subtle">Planned, on nobody</span>
            </th>
            {report.weeks.map((week) => {
              const row = report.unassigned.find((entry) => entry.weekStart === week);
              const taskCount = row?.taskCount ?? 0;
              const points = row?.points ?? 0;

              return (
                <td key={week} className="border-l border-line px-3 py-3 align-top">
                  {taskCount === 0 ? (
                    <span className="font-mono text-[0.8125rem] text-subtle tnum">0</span>
                  ) : (
                    <span className="inline-flex flex-col gap-1">
                      <span className="inline-flex w-fit items-center gap-1.5 rounded-full border border-dashed border-warn/50 bg-warn-soft px-2 py-0.5 text-[0.6875rem] font-semibold text-warn-ink">
                        <Icon name="alert" size={11} />
                        {formatCount(taskCount, 'task')}
                      </span>
                      <span className="font-mono text-[0.6875rem] text-muted tnum">
                        {formatPoints(points)} pts unowned
                      </span>
                    </span>
                  )}
                </td>
              );
            })}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* -------------------------------------------------------------------------- */

function Cell({ cell, isThisWeek }: { cell: CapacityCell; isThisWeek: boolean }) {
  const over = cell.overBy > 0;
  const idle = cell.assignedPoints === 0 && cell.unestimatedTasks === 0;

  /*
   * The meter is scaled to whichever is larger, the commitment or the
   * capacity, so an over-committed bar genuinely overshoots the rule marking
   * capacity rather than pinning at 100% and looking merely full.
   */
  const scale = Math.max(cell.capacityPoints, cell.assignedPoints, 1);
  const withinPct = (Math.min(cell.assignedPoints, cell.capacityPoints) / scale) * 100;
  const overPct = (Math.max(0, cell.assignedPoints - cell.capacityPoints) / scale) * 100;
  const rulePct = (cell.capacityPoints / scale) * 100;

  return (
    <td
      className={cn(
        'relative border-l border-line px-3 py-3 align-top',
        isThisWeek && 'bg-accent-soft/25',
        over && 'bg-danger-soft/60',
      )}
    >
      {over ? (
        <span className="hatch-blocked pointer-events-none absolute inset-0" aria-hidden="true" />
      ) : null}

      <div className="relative flex flex-col gap-1.5">
        <div className="flex items-baseline gap-1.5">
          <span
            className={cn(
              'font-mono text-[0.9375rem] leading-none tnum',
              over ? 'font-semibold text-danger-ink' : idle ? 'text-subtle' : 'text-ink',
            )}
          >
            {formatPoints(cell.assignedPoints)}
          </span>
          <span className="font-mono text-[0.6875rem] text-subtle tnum">
            / {formatPoints(cell.capacityPoints)}
          </span>
        </div>

        <div
          className="relative h-1.5 w-full overflow-hidden rounded-full bg-sunken"
          role="img"
          aria-label={`${formatPoints(cell.assignedPoints)} of ${formatPoints(
            cell.capacityPoints,
          )} points assigned`}
        >
          <div className="flex h-full w-full">
            <div
              className="h-full"
              style={{
                width: `${withinPct}%`,
                backgroundColor: over ? 'var(--danger)' : 'var(--lane-in_progress)',
              }}
            />
            {overPct > 0 ? (
              <div
                className="hatch-blocked h-full border-l border-danger"
                style={{ width: `${overPct}%`, backgroundColor: 'var(--danger-soft)' }}
              />
            ) : null}
          </div>
          {/* The line capacity sits on. Visible whenever the fill crosses it. */}
          {overPct > 0 ? (
            <span
              aria-hidden="true"
              className="absolute inset-y-0 w-px bg-ink/50"
              style={{ left: `${rulePct}%` }}
            />
          ) : null}
        </div>

        {over ? (
          <span className="inline-flex w-fit items-center gap-1 rounded-full border border-danger/40 bg-danger-soft px-2 py-0.5 text-[0.6875rem] font-semibold text-danger-ink">
            <Icon name="alert" size={11} />
            Over by {formatPoints(cell.overBy)}
          </span>
        ) : null}

        {cell.unestimatedTasks > 0 ? (
          <span
            className="inline-flex w-fit items-center gap-1 rounded-sm border border-dashed border-line-strong px-1.5 py-0.5 font-mono text-[0.6875rem] text-muted tnum"
            title={`${formatCount(cell.unestimatedTasks, 'task')} with no estimate — not counted in the points above`}
          >
            ? {cell.unestimatedTasks} unestimated
          </span>
        ) : null}

        {idle ? <span className="text-[0.6875rem] text-subtle">Nothing planned</span> : null}
      </div>
    </td>
  );
}

/** What the markings mean, next to the thing they mark. */
export function CapacityLegend() {
  return (
    <ul className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-[0.75rem] text-subtle">
      <li className="flex items-center gap-2">
        <span
          className="hatch-blocked inline-block size-4 rounded-xs border border-danger/40"
          aria-hidden="true"
        />
        Over capacity — hatched, flagged and named, so it reads without colour
      </li>
      <li className="flex items-center gap-2">
        <span
          className="inline-block rounded-sm border border-dashed border-line-strong px-1 font-mono text-[0.625rem]"
          aria-hidden="true"
        >
          ?
        </span>
        Tasks with no estimate — real work, absent from the points total
      </li>
    </ul>
  );
}
